<template>
  <content-popup
    class="search-filter-popup"
    :title="'Filters'"
    :visible="visible"
    :reference-element="contentPopupRefElement"
    boundary=".add-teacher-from-group-content-subpage"
    primary-button-label="APPLY"
    secondary-button-label="RESET"
    :show-close-button="true"
    @on-confirm="updateSearchFilter"
    @on-cancel="handleReset"
    @on-close="handleClose"
  >
    <template #content>
      <cv-form>
        <dl v-if="filterFlags.school" class="field-set type-dropdown">
          <dt class="field-label">School</dt>
          <dd class="field-input">
            <multi-select
              class="schools-filter"
              :label="t('add_recipient_from_school_filter_school_label')"
              selection-feedback="fixed"
              :options="schools"
              :value="schoolSelected"
              @change="handleSchoolChange"
            />
          </dd>
        </dl>
        <dl v-if="filterFlags.role" class="field-set type-dropdown">
          <dt class="field-label">Role</dt>
          <dd class="field-input">
            <multi-select
              class="role-filter"
              :label="t('add_recipient_from_school_filter_role_label')"
              selection-feedback="fixed"
              :options="roles"
              :value="roleSelected"
              @change="handleRoleChange"
            />
          </dd>
        </dl>
      </cv-form>
    </template>
  </content-popup>
</template>

<script setup lang="ts">
import { ref, onMounted, watch, nextTick } from "vue";
import { useI18n } from "vue-i18n";
import DOMUtils from "@utils/dom.utils";

// Composables
const { t } = useI18n();

// Emits
const emit = defineEmits<{
  (e: "change", payload: { school: unknown[]; role: unknown[] }): void;
  (e: "close"): void;
}>();

// Data
const contentPopupRefElement = ref<HTMLElement | null>(null);
const roleSelected = ref<unknown[]>([]);
const schoolSelected = ref<unknown[]>([]);

// Props
interface Props {
  roles?: unknown[];
  schools?: unknown[];
  filterFlags: Record<string, unknown>;
  appliedSearchFilters: Record<string, unknown>;
  visible: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  roles: () => [],
  schools: () => [],
});

// Watchers
watch(
  () => props.appliedSearchFilters,
  () => {
    updateFilterSelection();
  },
  { deep: true },
);

// Methods
const updateSearchFilter = () => {
  emit("change", {
    school: schoolSelected.value,
    role: roleSelected.value,
  });
};

const handleReset = () => {
  schoolSelected.value = [];
  roleSelected.value = [];
};

const handleClose = () => {
  updateFilterSelection();
  emit("close");
};

const updateFilterSelection = () => {
  roleSelected.value = props.appliedSearchFilters.role ?? [];
  schoolSelected.value = props.appliedSearchFilters.school ?? [];
};

const handleSchoolChange = (school) => {
  schoolSelected.value = school;
};

const handleRoleChange = (role) => {
  roleSelected.value = role;
};

const getReferenceContainer = () => {
  return (
    DOMUtils.elOf(document, ".add-teacher-from-group-content-subpage .search-bar-wrapper")[0] ??
    null
  );
};

// Lifecycle
onMounted(() => {
  // From created: Run immediately
  updateFilterSelection();
  updateSearchFilter();

  // From mounted: nextTick for DOM access
  nextTick(() => {
    contentPopupRefElement.value = getReferenceContainer();
  });
});
</script>
